import { useState } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate, Link } from "react-router-dom";
import "./VendorLogin.css";

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19.5C5 19.5 1.5 12 1.5 12a20.7 20.7 0 0 1 4.66-5.94M9.9 4.7A10.4 10.4 0 0 1 12 4.5c7 0 10.5 7.5 10.5 7.5a20.6 20.6 0 0 1-2.35 3.4M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <path d="M1 1l22 22" />
  </svg>
);

export default function VendorLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    try {
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;

      const userId = authData.user.id;

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", userId)
        .single();

      if (profileError || !profile) throw new Error("No role assigned. Contact support.");
      if (profile.role !== "vendor") throw new Error("Access denied: you are not a vendor.");

      navigate("/vendor/dashboard");
    } catch (err) {
      console.error("Vendor login error:", err);
      setErrorMsg(err.message || "Failed to login. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Routed through our own endpoint (generateLink + our own email sender)
  // instead of supabase.auth.resetPasswordForEmail, which sends through
  // Supabase's own mailer/template — that's what was showing up un-whitelabeled.
  const handleForgotPassword = async () => {
    if (!email) {
      setErrorMsg("Enter your email above first, then tap 'Forgot password'.");
      return;
    }
    setErrorMsg("");
    setLoading(true);

    try {
      await fetch("/api/vendor-request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      console.error("Reset request failed:", err);
      // Still show the generic success message — don't leak request
      // failures either, same reasoning as the endpoint itself.
    }

    setLoading(false);
    setResetSent(true);
  };

  return (
    <div className="vendor-login-page">
      <div className="shape shape-blob-1" aria-hidden="true" />
      <div className="shape shape-blob-2" aria-hidden="true" />
      <div className="shape shape-ring" aria-hidden="true" />
      <div className="shape shape-dot-1" aria-hidden="true" />
      <div className="shape shape-dot-2" aria-hidden="true" />
      <div className="shape shape-triangle" aria-hidden="true" />

      <div className="login-card">
        <span className="eyebrow">Biz Baz Events</span>
        <h1>Vendor Login</h1>

        {resetSent ? (
          <p className="reset-sent-msg">
            If an account exists for that email, a reset link is on its way.
          </p>
        ) : (
          <form onSubmit={handleLogin} noValidate>
            {errorMsg && (
              <p className="error" role="alert">
                {errorMsg}
              </p>
            )}

            <div className="field">
              <label htmlFor="vendor-email">Email</label>
              <input
                id="vendor-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="vendor-password">Password</label>
              <div className="password-input">
                <input
                  id="vendor-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="reveal-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? <span className="spinner" aria-hidden="true" /> : "Log in"}
            </button>

            <button
              type="button"
              className="forgot-password-link"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              Forgot password?
            </button>
          </form>
        )}

        <p className="signup-prompt">
          No account? <Link to="/vendor/signup">Sign up</Link>
        </p>
      </div>
    </div>
  );
}